import * as React from 'react';
import { ColumnOrderState, HeaderGroup } from '@tanstack/react-table';

import { NewTableColumn, NewTableColumnId, NewTableDefaultDataItem, NewTableGetId, NewTableLoadMetadata, NewTableLoadMetadataSorting, NewTableLoading, NewTableSortingDirection } from '@common/components/new-table/new-table.types';

export type HeaderProps = {
  loading: NewTableLoading;
  currentColumnResizeOffset?: number;
  loadTableMetadata?: NewTableLoadMetadata;
  model: Array<HeaderGroup<NewTableDefaultDataItem>>;
  columns: Array<NewTableColumn<NewTableDefaultDataItem>>;
  getId: NewTableGetId;
  onColumnResizeEnd?: () => void;
  onSort: (sorting: NewTableLoadMetadataSorting) => Promise<void> | void;
  onCellContextMenu?: (params: { 
    columnId: NewTableColumn<NewTableDefaultDataItem>['id'];
    coords: {
      x: number;
      y: number;
    };
  }) => void;
  onHeaderContextMenu?: (params: { 
    coords: {
      x: number;
      y: number;
    };
  }) => void;
  onColumnsOrderChange?: (order: ColumnOrderState) => void;
};

export const useController = (props: HeaderProps) => {
  const { loadTableMetadata, columns, onSort, onCellContextMenu, onHeaderContextMenu, onColumnsOrderChange } = props;

  const reorderColumn = React.useCallback((
    draggedColumnId: string,
    targetColumnId: string,
    order: NewTableColumnId[]
  ): ColumnOrderState => {
    const draggedColumnIndex = order.findIndex((id) => id === draggedColumnId);
    const targetColumnIndex = order.findIndex((id) => id === targetColumnId);

    const fromLeftToRight = draggedColumnIndex < targetColumnIndex;

    const orderCopy: Array<NewTableColumnId> = [];

    order.forEach((columnId) => {
      if (columnId === draggedColumnId) {
        return;
      }

      if (columnId === targetColumnId) {
        if (fromLeftToRight) {
          orderCopy.push(targetColumnId, draggedColumnId);
        } else {
          orderCopy.push(draggedColumnId, targetColumnId);
        }

        return;
      }

      orderCopy.push(columnId);
    });

    // Ensure column with field id -500 is always last
    const specialPrefix = "field_id__-500";
    const specials = orderCopy.filter((id) => id.startsWith(specialPrefix));
    const filtered = orderCopy.filter((id) => !id.startsWith(specialPrefix));

    return [...filtered, ...specials];
  }, []);

  const handleColumnDrop = React.useCallback((draggedColumnId: string, targetColumnId: string) => {
    if (draggedColumnId === targetColumnId) {
      return;
    } 

    const columnsOrder = columns.map((x) => x.id);
    const newColumnOrder = reorderColumn(
      draggedColumnId,
      targetColumnId,
      columnsOrder || []
    );

    if (onColumnsOrderChange) {
      onColumnsOrderChange(newColumnOrder);
    }
  }, [columns, onColumnsOrderChange, reorderColumn]);

  const handleSort = React.useCallback((columnId: NewTableColumnId) => {
    let nextSorting: NewTableLoadMetadataSorting;;

    if (loadTableMetadata?.sorting?.id === columnId) {
      nextSorting = {
        id: columnId,
        direction: loadTableMetadata.sorting.direction === NewTableSortingDirection.desc 
          ? NewTableSortingDirection.asc
          : NewTableSortingDirection.desc
      };
    } else {
      nextSorting = {
        id: columnId,
        direction: loadTableMetadata?.sorting?.direction || NewTableSortingDirection.asc
      };
    }

    onSort(nextSorting);
  }, [loadTableMetadata?.sorting, onSort]);

  const handleCellContextMenu = React.useCallback((e: React.MouseEvent<HTMLTableCellElement>, columnId: NewTableColumnId) => {
    e.stopPropagation();
    e.preventDefault();

    if (onCellContextMenu) {
      onCellContextMenu({
        columnId,
        coords: {
          x: e.clientX,
          y: e.clientY,
        }
      });
    }
  }, [onCellContextMenu]);

  const handleHeaderContextMenu = React.useCallback((e: React.MouseEvent<HTMLTableRowElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (onHeaderContextMenu) {
      onHeaderContextMenu({
        coords: {
          x: e.clientX,
          y: e.clientY,
        }
      });
    }
  }, [onHeaderContextMenu]);

  return {
    handleSort,
    handleCellContextMenu: onCellContextMenu ? handleCellContextMenu : undefined,
    handleHeaderContextMenu: onHeaderContextMenu ? handleHeaderContextMenu : undefined,
    handleColumnDrop: onColumnsOrderChange ? handleColumnDrop : undefined,
  };
};